/*
  Free UK delivery bar + cart add-ons (Armstrong Music)

  1. Keeps the free delivery bar in the "Added to cart" popup up to date.
  2. <cart-free-delivery> on the cart page re-renders itself after any cart change.
  3. <cart-addons> shows up to 4 suggested items under the cart. Mix of:
  - Search & Discovery complementary products for items in the cart (shown first)
  - the hand-picked "cart-add-ons" collection as a fallback / top-up

  Items are ranked by how well they close the gap to the next free delivery
  threshold: the cheapest item that gets the customer over the line comes first,
  then items just under the gap.
*/
(function () {
  if (window.__amFreeDelivery) return;
  window.__amFreeDelivery = true;

  const MAX_CARDS = 4;
  const MAX_COMPLEMENTARY = 2;
  const MAX_SOURCE_PRODUCTS = 3;
  const FALLBACK_COLLECTION = 'cart-add-ons';
  const cache = new Map();

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function fetchCards(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('.cart-addon'));
      })
      .catch(() => []);
  }

  function rankByGap(cards, gap) {
    const price = (c) => parseInt(c.dataset.price, 10) || 0;
    if (gap <= 0) return cards.slice().sort((a, b) => price(a) - price(b));
    const over = cards.filter((c) => price(c) >= gap).sort((a, b) => price(a) - price(b));
    const under = cards.filter((c) => price(c) < gap).sort((a, b) => price(b) - price(a));
    return over.concat(under);
  }

  class CartAddons extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    get productIds() {
      try {
        return JSON.parse(this.dataset.productIds || '[]').map(String);
      } catch (e) {
        return [];
      }
    }

    async getCandidates(ids) {
      const key = ids.join(',');
      if (cache.has(key)) return cache.get(key);

      const requests = ids.slice(0, MAX_SOURCE_PRODUCTS).map((id) =>
        fetchCards(`${root}recommendations/products?product_id=${id}&limit=10&intent=complementary&section_id=cart-addons`)
      );
      requests.push(fetchCards(`${root}collections/${FALLBACK_COLLECTION}?section_id=cart-addons`));

      const groups = await Promise.all(requests);
      const result = { complementary: [], collection: [] };
      const seen = new Set();

      groups.flat().forEach((card) => {
        const id = card.dataset.productId;
        if (seen.has(id)) return;
        seen.add(id);
        (card.dataset.source === 'complementary' ? result.complementary : result.collection).push(card);
      });

      cache.set(key, result);
      return result;
    }

    async load() {
      const list = this.querySelector('.cart-addons__list');
      if (!list) return;

      const ids = this.productIds;
      if (!ids.length) return;

      const inCart = new Set(ids);
      const gap = parseInt(this.dataset.gap, 10) || 0;
      const { complementary, collection } = await this.getCandidates(ids);

      const notInCart = (c) => !inCart.has(c.dataset.productId);
      const comp = rankByGap(complementary.filter(notInCart), gap).slice(0, MAX_COMPLEMENTARY);
      const usedIds = new Set(comp.map((c) => c.dataset.productId));
      const rest = rankByGap(
        complementary.filter(notInCart).concat(collection.filter(notInCart)).filter((c) => !usedIds.has(c.dataset.productId)),
        gap
      );
      const picks = comp.concat(rest).slice(0, MAX_CARDS);

      if (!picks.length) return;

      list.innerHTML = '';
      picks.forEach((card) => list.appendChild(document.importNode(card, true)));
      this.hidden = false;
      this.bindButtons();
    }

    bindButtons() {
      this.querySelectorAll('[data-addon-add]').forEach((button) => {
        button.addEventListener('click', () => this.add(button));
      });
    }

    async add(button) {
      const card = button.closest('.cart-addon');
      if (!card || button.disabled) return;
      button.disabled = true;
      button.classList.add('is-loading');

      const cartItems = document.querySelector('cart-items');
      const sections = cartItems && typeof cartItems.getSectionsToRender === 'function' ? cartItems.getSectionsToRender() : [];

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(card.dataset.variantId, 10), quantity: 1 }],
            sections: sections.map((s) => s.section),
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (state.status) throw new Error(state.description || 'Could not add item');

        if (state.sections && sections.length) {
          sections.forEach((section) => {
            const html = state.sections[section.section];
            const host = document.getElementById(section.id);
            if (!html || !host) return;
            const target = host.querySelector(section.selector) || host;
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
            if (fresh) target.innerHTML = fresh.innerHTML;
          });
        } else {
          window.location.reload();
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        }
      } catch (e) {
        console.error(e);
        button.disabled = false;
        button.classList.remove('is-loading');
        const label = button.querySelector('.cart-addon__add-label');
        if (label) label.textContent = 'Try again';
      }
    }
  }

  if (!customElements.get('cart-addons')) customElements.define('cart-addons', CartAddons);

  /* Cart page wrapper: re-render bar + add-ons whenever the cart changes */
  class CartFreeDelivery extends HTMLElement {
    connectedCallback() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    refresh() {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      fetch(`${cartUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}`, { credentials: 'same-origin' })
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-free-delivery');
          if (fresh) this.innerHTML = fresh.innerHTML;
        })
        .catch((e) => console.error(e));
    }
  }

  if (!customElements.get('cart-free-delivery')) customElements.define('cart-free-delivery', CartFreeDelivery);

  /* "Added to cart" popup: refresh the compact bar after each add */
  function refreshPopupBar() {
    const host = document.getElementById('free-delivery-popup');
    if (!host) return;
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(`${cartUrl}?section_id=free-delivery-bar`, { credentials: 'same-origin' })
      .then((r) => r.text())
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        host.innerHTML = section ? section.innerHTML : '';
      })
      .catch((e) => console.error(e));
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && document.getElementById('free-delivery-popup')) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshPopupBar);
  }
})();
